import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useQuery } from '@apollo/client/react';
import {
  applyStorefrontCatalogShopVisibility,
  getEnvShopId,
  getExtraCatalogShopIds,
  MULTI_SHOP_ENABLED,
} from '@core/config/shops';
import {
  apolloContextForShop,
  apolloContextUnscopedShopList,
} from '@core/api/apolloContext';
import { GET_MST_SHOP_BY_PK, GET_MST_SHOPS } from './queries';
import type { GetMstShopByPkResponse, GetMstShopsResponse, TenantShop, TenantShopRow } from './types';
import { setTenantShopsSnapshot } from './registry';
import { formatShopPhone, formatShopScalar } from './shopRowUtils';

export interface TenantShopsContextValue {
  shops: TenantShop[];
  loading: boolean;
  error?: unknown;
}

const TenantShopsContext = createContext<TenantShopsContextValue | null>(null);

function rowToTenant(row: TenantShopRow): TenantShop {
  const label =
    row.shop_name?.trim() ||
    row.name?.trim() ||
    '';
  const logoRef =
    row.shop_logo?.trim() ||
    row.photo?.trim() ||
    undefined;
  const pincode = formatShopScalar(row.pincode);
  const phone = formatShopPhone(row.mobile_number);
  const email = row.email?.trim() || undefined;

  return {
    id: row.id,
    name: label,
    logoRef,
    description: row.description?.trim() || undefined,
    businessAddress: row.business_address?.trim() || undefined,
    city: row.city?.trim() || undefined,
    state: row.state?.trim() || undefined,
    pincode,
    phone,
    email,
  };
}

const emptyShopStub = (id: string): TenantShop => ({
  id,
  name: '',
});

function mergeExtraShopIds(base: TenantShop[]): TenantShop[] {
  const extra = getExtraCatalogShopIds();
  if (!extra.length) return base;
  const seen = new Set(base.map((s) => s.id));
  const out = [...base];
  for (const id of extra) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(emptyShopStub(id));
    }
  }
  return out;
}

export function TenantShopsProvider({ children }: { children: ReactNode }) {
  const qList = useQuery<GetMstShopsResponse>(GET_MST_SHOPS, {
    skip: !MULTI_SHOP_ENABLED,
    fetchPolicy: 'cache-and-network',
    context: apolloContextUnscopedShopList(),
  });

  const envId = getEnvShopId();
  const qOne = useQuery<GetMstShopByPkResponse>(GET_MST_SHOP_BY_PK, {
    skip: MULTI_SHOP_ENABLED,
    variables: { id: envId },
    fetchPolicy: 'cache-and-network',
    context: apolloContextForShop(envId),
  });

  const shopsCore = useMemo((): TenantShop[] => {
    const withVisibility = (list: TenantShop[]) =>
      applyStorefrontCatalogShopVisibility(mergeExtraShopIds(list));

    if (MULTI_SHOP_ENABLED) {
      const rows = qList.data?.mst_shop ?? [];
      const mapped = rows.map(rowToTenant);
      if (mapped.length > 0) return withVisibility(mapped);
      if (qList.loading) return [];
      return withVisibility([emptyShopStub(envId)]);
    }
    const row = qOne.data?.mst_shop_by_pk;
    if (row) return withVisibility([rowToTenant(row)]);
    return withVisibility([emptyShopStub(envId)]);
  }, [
    MULTI_SHOP_ENABLED,
    envId,
    qList.data,
    qList.loading,
    qList.error,
    qOne.data,
    qOne.loading,
    qOne.error,
  ]);

  const shops = shopsCore;

  const loading = MULTI_SHOP_ENABLED ? qList.loading : qOne.loading;
  const error = (MULTI_SHOP_ENABLED ? qList.error : qOne.error) ?? undefined;

  useEffect(() => {
    setTenantShopsSnapshot(shops);
  }, [shops]);

  const value = useMemo(
    (): TenantShopsContextValue => ({
      shops,
      loading,
      error: error ?? undefined,
    }),
    [shops, loading, error]
  );

  return (
    <TenantShopsContext.Provider value={value}>
      {children}
    </TenantShopsContext.Provider>
  );
}

export function useTenantShops(): TenantShopsContextValue {
  const ctx = useContext(TenantShopsContext);
  if (!ctx) {
    throw new Error('useTenantShops must be used within TenantShopsProvider');
  }
  return ctx;
}
